import { PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/20/solid'
import { useTranslation } from 'react-i18next'

const formatStartTime = value => {
	const match = `${value || ''}`.trim().match(/^(\d{1,2}):(\d{2})/)
	if (!match) return ''
	return `${match[1].padStart(2, '0')}:${match[2]}`
}

export default function ProjectStepsSection({
	steps = [],
	onAdd,
	onEdit,
	onAddNextDay,
	onRemove,
}) {
	const { t } = useTranslation()
	const rows = Array.isArray(steps) ? steps : []

	return (
		<div className="mt-6 rounded-md border border-gray-200 bg-white px-5 py-4 shadow-sm">
			<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
				<div>
					<h2 className="text-sm font-semibold text-gray-900">
						{t('projects.details.sections.plan', { defaultValue: 'Plan projektu' })}
					</h2>
					<p className="mt-1 text-sm text-gray-500">
						{t('projects.details.steps.description', { defaultValue: 'Dodawaj, edytuj i usuwaj etapy projektu. Każdy etap działa jak jedno szkolenie.' })}
					</p>
				</div>
				<button
					type="button"
					onClick={onAdd}
					className="inline-flex items-center gap-1 rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700"
				>
					<PlusIcon className="h-4 w-4" />
					{t('projects.details.steps.add', { defaultValue: 'Dodaj etap' })}
				</button>
			</div>

			<div className="mt-4 space-y-3">
				{rows.length ? rows.map((step, index) => {
					const startDate = step.data_rozpoczecia
						? `${step.data_rozpoczecia}`.substring(0, 10)
						: '—'
					const pozycje = Array.isArray(step.pozycje) ? step.pozycje : []
					const trainers = Array.isArray(step.trainerNames) ? step.trainerNames : pozycje.map(item => item.trainerName).filter(Boolean)
					const key = step._id || `step-${index}`

					return (
						<div
							key={key}
							className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3"
						>
							<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
								<div className="min-w-0 flex-1">
									<h3 className="text-sm font-semibold text-gray-900">
										{t('projects.details.steps.stepLabel', { defaultValue: 'Etap {{number}}', number: index + 1 })}
									</h3>
									<div className="mt-2 grid grid-cols-1 gap-1 text-sm text-gray-600 sm:grid-cols-4">
										<div>
											<span className="font-medium text-gray-700">
												{t('projects.form.fields.stepDate', { defaultValue: 'Data etapu' })}:
											</span>{' '}
											{startDate}
										</div>
										<div>
											<span className="font-medium text-gray-700">
												{t('projects.list.table.placowkaName')}:
											</span>{' '}
											{step.placowkaName || '—'}
										</div>
										<div>
											<span className="font-medium text-gray-700">
												{t('projects.form.positionsSection.title', { defaultValue: 'Pozycje' })}:
											</span>{' '}
											{pozycje.length}
										</div>
										<div>
											<span className="font-medium text-gray-700">
												{t('projects.list.table.trainerName')}:
											</span>{' '}
											{trainers.length ? trainers.join(', ') : t('projects.list.table.unassignedTrainer')}
										</div>
									</div>
									{pozycje.some(item => formatStartTime(item.start_time) || formatStartTime(item.end_time)) ? (
										<ul className="mt-2 space-y-1 text-sm text-gray-600">
											{pozycje.map((pozycja, pozycjaIndex) => {
												const startTime = formatStartTime(pozycja.start_time)
												const endTime = formatStartTime(pozycja.end_time)
												if (!startTime && !endTime) return null
												const title = `${pozycja.value || ''}`.trim()
												return (
													<li key={pozycja._id || `pozycja-${pozycjaIndex}`}>
														<span className="font-medium text-gray-700">
															{title || t('projects.form.positionsSection.itemLabel', {
																defaultValue: 'Pozycja {{number}}',
																number: pozycjaIndex + 1,
															})}
														</span>
														{startTime ? (
															<>
																{' · '}
																{t('projects.form.positionsSection.startTime', { defaultValue: 'Godzina startu' })}
																{': '}
																{startTime}
															</>
														) : null}
														{endTime ? (
															<>
																{' · '}
																{t('projects.form.positionsSection.endTime', { defaultValue: 'Godzina zakończenia' })}
																{': '}
																{endTime}
															</>
														) : null}
													</li>
												)
											})}
										</ul>
									) : null}
									{step.osoba_kontaktowa?.nazwisko || step.osoba_kontaktowa?.telefon ? (
										<p className="mt-2 text-sm text-gray-500">
											<span className="font-medium text-gray-700">
												{t('projects.form.contactPerson.title', { defaultValue: 'Osoba kontaktowa' })}:
											</span>{' '}
											{[
												step.osoba_kontaktowa?.nazwisko,
												step.osoba_kontaktowa?.funkcja,
												step.osoba_kontaktowa?.telefon,
											].filter(Boolean).join(' · ')}
										</p>
									) : null}
									{step.notatki ? (
										<p className="mt-2 whitespace-pre-line text-sm text-gray-500">
											<span className="font-medium text-gray-700">
												{t('projects.form.fields.stepNotes', { defaultValue: 'Notatki' })}:
											</span>{' '}
											{step.notatki}
										</p>
									) : null}
								</div>
								<div className="flex flex-wrap items-center gap-2">
									{onAddNextDay ? (
										<button
											type="button"
											onClick={() => onAddNextDay(step)}
											className="inline-flex items-center gap-1 rounded-md border border-indigo-100 bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100"
										>
											<PlusIcon className="h-4 w-4" />
											{t('projects.details.steps.duplicate', { defaultValue: 'Powiel etap' })}
										</button>
									) : null}
									<button
										type="button"
										onClick={() => onEdit(step, index)}
										className="inline-flex items-center gap-1 rounded-md border border-blue-100 bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100"
									>
										<PencilIcon className="h-4 w-4" />
										{t('projects.list.table.edit')}
									</button>
									<button
										type="button"
										onClick={() => onRemove(step, index)}
										className="inline-flex items-center gap-1 rounded-md border border-red-100 bg-red-50 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100"
									>
										<TrashIcon className="h-4 w-4" />
										{t('projects.list.table.delete')}
									</button>
								</div>
							</div>
						</div>
					)
				}) : (
					<div className="rounded-md border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500">
						{t('projects.details.steps.empty', { defaultValue: 'Brak etapów. Dodaj pierwszy etap planu projektu.' })}
					</div>
				)}
			</div>
		</div>
	)
}
